import { Address } from "./address";
import { Account } from "./account";
import { ContratStatus, MoyenDePaiement, PeriodiciteStatus, TypeContrat, PriseDeContactType, JetonLLType, JetonSLType } from "./enum-type";
import { Entity } from "./entity";

export interface Laverie extends Entity {
	accountId: string
	numeroDeContrat: number
	dateDeDemarrage: Date
	dateDeRenouvelement: Date
	typeContrat: TypeContrat
	siret?: string
	dureeDuContrat: number
	periodicity?: PeriodiciteStatus
	codeTournee?: string
	contratStatus?: ContratStatus
	telephonePrincipal: string
	telephoneAutre?: string
	email: string
	website?: string
	gestionnaire: string
	codeAcces?: string
	tarifLavage: number
	tarifSechage: number
	tarifLessive: number
	tempsSechage: number
	locationLaveLinge: number
	locationSecheLinge: number
	locationMonnayeur: number
	locationArmoirePompeLessive: number
	locationCentralePaiement: number
	seuil: number
	tauxDeRedevance: number
	moyenDePaiement: MoyenDePaiement
	address: Address
	nbLaveLinge: number
	nbSecheLinge: number
	nbLaveLingeChauffant: number
	nbLintTrap: number
	nbCondensateur: number
	nbArmoireLessive: number
	nbPompeLessive:number
	nbStackedLave: number
	nbStackedSeche: number
	nbCentrale: number
	nbMonnayeur: number
	nbBallonEauChaude: number
	notificationEnabled: boolean
	priseDeContactType: PriseDeContactType
	priseDeContactDate: Date
	previsiteDate: Date
    previsiteBy: string
    previsitText: string
    previsiteFileName: string
	dateSignatureContrat: Date
	installateur: string
	bonInstallationFileName: string
	typePriseLL: string
	typePriseSL: string
	cleCaisse: string
	cleDeProgrammation: string
	observationTechnique: string
	verifTravaux: string
	jetonLLType: JetonLLType
	jetonSLType: JetonSLType
	terminationDate: Date
	installationDate: Date
	collectionDate: Date
	fullAddress?: string
	externalId: string
}

export class LaverieExport {
	lavs: Laverie[];
}

export class LaverieGen {
	// numero de contrat
	nc: string
	hnc: boolean

	// type contrat
	tc: string
	htc: boolean

	// Account siret
	as: string
	has: boolean

	// Account n° & rue
	anr: string
	hanr: boolean

	// Account code postale & ville
	acv: string
	hacv: boolean

	// Account signataire
	asi: string
	hasi: boolean

	// Account fonction du signataire
	asq: string
	hasq: boolean

	// Laverie nom
	ln: string
	hln: boolean

	// Laverie n° & rue
	lnr: string
	hlnr: boolean

	// Laverie code postale & ville
	lcv: string
	hlcv: boolean

	// Taux de redevance
	tdr: string
	htdr: boolean

	// Duréé du contrat
	d: string
	hd: boolean

	// Nb & product type
	p: string[]

	// Rent price with product type
	r: string[]
}

function getProductsNbAndType(laverie: Laverie): string[] {
	const result = [] as string[]
	if (laverie.nbLaveLinge) {
		result.push(laverie.nbLaveLinge.toString().concat(" lave linge(s)"))
	}
	if (laverie.nbSecheLinge) {
		result.push(laverie.nbSecheLinge.toString().concat(" sèche linge(s)"))
	}
	if (laverie.nbLaveLingeChauffant) {
		result.push(laverie.nbLaveLingeChauffant.toString().concat(" lave linge chauffant(s)"))
	}
	if (laverie.nbLintTrap) {
		result.push(laverie.nbLintTrap.toString().concat(" Lint Trap("))
	}
	if (laverie.nbCondensateur) {
		result.push(laverie.nbCondensateur.toString().concat(" condensateur(s)"))
	}
	if (laverie.nbArmoireLessive) {
		result.push(laverie.nbArmoireLessive.toString().concat(" armoire à lessive"))
	}
	if (laverie.nbPompeLessive) {
		result.push(laverie.nbPompeLessive.toString().concat(" pompe à lessive"))
	}
	if (laverie.nbStackedLave) {
		result.push(laverie.nbStackedLave.toString().concat(" stacked lavante"))
	}
	if (laverie.nbStackedSeche) {
		result.push(laverie.nbStackedSeche.toString().concat(" stacked sechante"))
	}
	if (laverie.nbCentrale) {
		result.push(laverie.nbCentrale.toString().concat(" centrale"))
	}
	if (laverie.nbMonnayeur) {
		result.push(laverie.nbMonnayeur.toString().concat(" monayeur"))
	}
	if(laverie.nbBallonEauChaude) {
		result.push(laverie.nbBallonEauChaude.toString().concat(" ballon d'eau chaude"))
	}
	return result
}

function getProductsPriceAndType(laverie: Laverie): string[] {
	const result = [] as string[]

	if (laverie.locationLaveLinge) {
		result.push("Lave linge ".concat(laverie.locationLaveLinge.toString()).concat(" €"))
	}

	if (laverie.locationSecheLinge) {
		result.push("Sèche linge ".concat(laverie.locationSecheLinge.toString()).concat(" € (")
			.concat(laverie.tempsSechage.toString()).concat(" minutes)"))
	}
	if (laverie.locationMonnayeur) {
		result.push("Terminal de paiement ".concat(laverie.locationMonnayeur.toString()).concat(" €"))
	}
	if (laverie.locationArmoirePompeLessive) {
		result.push("Armoire à lessive et de la pompe ".concat(laverie.locationArmoirePompeLessive.toString()).concat(" €"))
	}

	return result
}

export function getLaverieExport(laveries: Laverie[]): LaverieExport {
    const laverieExport = new LaverieExport()
    laverieExport.lavs = laveries;
    return laverieExport
}

export function getLaverieGen(account: Account, laverie: Laverie): LaverieGen {
	const lg = new LaverieGen()
	// Laverie numéro de contrat
	if(laverie.numeroDeContrat){
		lg.nc = laverie.numeroDeContrat.toString()
		lg.hnc = true
	}else{
		lg.hnc = false
	}

	// Laverie typ de contrat
	if(laverie.typeContrat){
		lg.tc = laverie.typeContrat
		lg.htc = true
	}else{
		lg.htc = false
	}

	// Account siret
	if(account.siret){
		lg.as = account.siret
		lg.has = true
	}else {
		lg.has = false
	}

	// Account n° & rue
	if (account.address.numero) {
		lg.anr = account.address.numero
	}
	if (account.address.rue) {
		if (lg.anr) {
			lg.anr = lg.anr.concat(" " + account.address.rue)
		} else {
			lg.anr = account.address.rue
		}
	}
	if (lg.anr) {
		lg.hanr = true
	} else {
		lg.hanr = false
	}

	// Account code postale & ville
	if (account.address.codepostale) {
		lg.acv = account.address.codepostale;
	}
	if (account.address.ville) {
		if (lg.acv) {
			lg.acv = lg.acv.concat(" " + account.address.ville)
		} else {
			lg.acv = account.address.ville
		}
	}
	if (lg.acv) {
		lg.hacv = true
	} else {
		lg.hacv = false
	}

	// Account signataire
	if (account.signataire) {
		lg.asi = account.signataire
		lg.hasi = true
	} else {
		lg.hasi = false
	}

	// Account signataire qualité
	if(account.fonctionSignataire){
		lg.asq = account.fonctionSignataire
		lg.hasq = true
	}else{
		lg.hasq = false
	}

	// Laverie nom
	if (laverie.name) {
		lg.ln = laverie.name
		lg.hln = true
	} else {
		lg.hln = false
	}

	// Laverie n° & rue
	if (laverie.address.numero) {
		lg.lnr = laverie.address.numero
	}
	if (laverie.address.rue) {
		if (lg.lnr) {
			lg.lnr = lg.lnr.concat(" " + laverie.address.rue)
		} else {
			lg.lnr = laverie.address.rue
		}
	}
	if (lg.lnr) {
		lg.hlnr = true
	} else {
		lg.hlnr = false
	}

	// Laverie code postale & ville
	if (laverie.address.codepostale) {
		lg.lcv = laverie.address.codepostale;
	}
	if (laverie.address.ville) {
		if (lg.lcv) {
			lg.lcv = lg.lcv.concat(" " + laverie.address.ville)
		} else {
			lg.lcv = laverie.address.ville
		}
	}
	if (lg.lcv) {
		lg.hlcv = true
	} else {
		lg.hlcv = false
	}

	// Taux de redevance
	if (laverie.tauxDeRedevance) {
		lg.tdr = laverie.tauxDeRedevance.toString()
		lg.htdr = true
	} else {
		lg.htdr = false
	}

	// Duréé du contrat
	if (laverie.dureeDuContrat) {
		lg.d = laverie.dureeDuContrat.toString()
		lg.hd = true
	} else {
		lg.hd = true
	}

	// Nb & product type
	lg.p = getProductsNbAndType(laverie)

	// Rent price with product type
	lg.r = getProductsPriceAndType(laverie)

	return lg;
}